<script setup>
import { ref, computed, watch } from 'vue'
import { useScrollLock } from '@/composables/useScrollLock'

const props = defineProps({
  subjects: {
    type: Array,
    required: true
  },
  levels: {
    type: Array,
    default: () => []
  },
  selectedSubjects: {
    type: Array,
    default: () => []
  },
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:selected'])

useScrollLock(() => props.show)

// Structure: { subject_id: { subject, level_ids: [] } }
const subjectLevelMap = ref({})
const validationErrors = ref({})

// Update local selection when modal opens or selectedSubjects prop changes
watch(() => props.show, (newShow) => {
  if (newShow) {
    initializeSubjectLevelMap()
    validationErrors.value = {}
  }
})

watch(() => props.selectedSubjects, (newSelected) => {
  if (props.show) {
    initializeSubjectLevelMap()
  }
}, { deep: true })

const initializeSubjectLevelMap = () => {
  const map = {}
  props.selectedSubjects.forEach(subject => {
    map[subject.id] = {
      subject: subject,
      level_ids: subject.level_ids || []
    }
  })
  subjectLevelMap.value = map
}

const isSubjectEnabled = (subject) => {
  return !!subjectLevelMap.value[subject.id]
}

const toggleSubject = (subject) => {
  if (subjectLevelMap.value[subject.id]) {
    // Remove subject
    delete subjectLevelMap.value[subject.id]
    delete validationErrors.value[subject.id]
  } else {
    // Add subject with no levels selected initially
    subjectLevelMap.value[subject.id] = {
      subject: subject,
      level_ids: []
    }
  }
}

const isLevelSelected = (subjectId, levelId) => {
  return subjectLevelMap.value[subjectId]?.level_ids.includes(levelId) || false
}

const toggleLevel = (subjectId, levelId) => {
  if (!subjectLevelMap.value[subjectId]) return

  const levelIds = subjectLevelMap.value[subjectId].level_ids
  const index = levelIds.indexOf(levelId)

  if (index > -1) {
    levelIds.splice(index, 1)
  } else {
    levelIds.push(levelId)
  }

  // Clear validation error for this subject when levels are selected
  if (levelIds.length > 0) {
    delete validationErrors.value[subjectId]
  }
}

const validateSelection = () => {
  const errors = {}

  Object.entries(subjectLevelMap.value).forEach(([subjectId, data]) => {
    if (data.level_ids.length === 0) {
      errors[subjectId] = 'Please select at least one level for this subject'
    }
  })

  validationErrors.value = errors
  return Object.keys(errors).length === 0
}

const handleSave = () => {
  if (!validateSelection()) {
    // Scroll to first error
    const firstErrorSubjectId = Object.keys(validationErrors.value)[0]
    const errorElement = document.getElementById(`subject-${firstErrorSubjectId}`)
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    return
  }

  // Convert map to array of subjects with level_ids
  const selectedSubjects = Object.values(subjectLevelMap.value).map(data => ({
    ...data.subject,
    level_ids: data.level_ids
  }))

  emit('update:selected', selectedSubjects)
  emit('close')
}

const handleCancel = () => {
  initializeSubjectLevelMap()
  validationErrors.value = {}
  emit('close')
}

const selectedCount = computed(() => Object.keys(subjectLevelMap.value).length)

// Helper to convert hex to RGB for opacity
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

// Get subject border and background styles
const getSubjectStyles = (subject) => {
  if (!subject.color) return {}
  const rgb = hexToRgb(subject.color)
  if (!rgb) return {}

  return {
    borderColor: subject.color,
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`
  }
}

// Get level button styles
const getLevelStyles = (level, isSelected) => {
  if (!level.color) return {}

  if (isSelected) {
    return {
      borderColor: level.color,
      backgroundColor: level.color,
      color: '#ffffff'
    }
  }

  const rgb = hexToRgb(level.color)
  if (!rgb) return {}

  return {
    borderColor: level.color,
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`,
    color: level.color
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          @click="handleCancel"
        ></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full transform transition-all"
            @click.stop
          >
            <!-- Header -->
            <div class="border-b border-gray-200 px-6 py-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-xl font-bold text-gray-900">Select Group Tutoring Subjects & Levels</h3>
                  <p class="text-sm text-gray-500 mt-1">Choose which subjects you'll offer and the levels you can teach</p>
                </div>
                <button
                  type="button"
                  @click="handleCancel"
                  class="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <span class="material-symbols-outlined text-2xl">close</span>
                </button>
              </div>
            </div>

            <!-- Body -->
            <div class="px-6 py-4 max-h-[32rem] overflow-y-auto">
              <div class="space-y-3">
                <div
                  v-for="subject in subjects"
                  :key="subject.id"
                  :id="`subject-${subject.id}`"
                  class="border-2 rounded-xl transition-all"
                  :class="[
                    validationErrors[subject.id] ? 'border-red-500 bg-red-50' : ''
                  ]"
                  :style="isSubjectEnabled(subject) ? getSubjectStyles(subject) : {}"
                >
                  <!-- Subject Row -->
                  <div class="flex items-center justify-between p-4 cursor-pointer" @click="toggleSubject(subject)">
                    <span class="text-sm font-medium text-gray-900">{{ subject.name }}</span>
                    <div class="relative inline-block w-11 h-6">
                      <input
                        type="checkbox"
                        :checked="isSubjectEnabled(subject)"
                        @change.stop="toggleSubject(subject)"
                        class="peer sr-only"
                      >
                      <div
                        class="w-11 h-6 bg-gray-200 rounded-full peer transition-colors"
                        :style="isSubjectEnabled(subject) && subject.color ? { backgroundColor: subject.color } : {}"
                      ></div>
                      <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </div>

                  <!-- Levels Selection (shown when subject is enabled) -->
                  <div v-if="isSubjectEnabled(subject)" class="px-4 pb-4 space-y-2" @click.stop>
                    <p class="text-xs text-gray-600 mb-2">Select levels you can teach:</p>
                    <div class="flex flex-wrap gap-2">
                      <button
                        v-for="level in levels"
                        :key="level.id"
                        type="button"
                        @click="toggleLevel(subject.id, level.id)"
                        class="px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all hover:opacity-90"
                        :style="getLevelStyles(level, isLevelSelected(subject.id, level.id))"
                      >
                        {{ level.name }}
                      </button>
                    </div>

                    <!-- Validation Error -->
                    <p v-if="validationErrors[subject.id]" class="text-xs text-red-600 mt-2 flex items-center gap-1">
                      <span class="material-symbols-outlined text-sm">error</span>
                      {{ validationErrors[subject.id] }}
                    </p>
                  </div>
                </div>

                <!-- No Subjects Message -->
                <div v-if="subjects.length === 0" class="text-center py-8 text-gray-500">
                  <span class="material-symbols-outlined text-4xl mb-2 block">school</span>
                  <p>No subjects available</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="border-t border-gray-200 px-6 py-4">
              <div class="flex items-center justify-between">
                <p class="text-sm text-gray-600">
                  <span class="font-semibold text-purple-600">{{ selectedCount }}</span> subject{{ selectedCount !== 1 ? 's' : '' }} selected
                </p>
                <div class="flex gap-3">
                  <button
                    type="button"
                    @click="handleCancel"
                    class="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    @click="handleSave"
                    class="px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Save Selection
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
